import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Paper,
  Snackbar,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import SyncIcon from '@mui/icons-material/Sync';
import { useAuth } from '../contexts/AuthContext';
import { useI18n } from '../i18n';
import { checkSubmittedOrderNow, fetchSubmittedOrders } from '../api/orderJobs';

const DEFAULT_HOURS = 12;
const MAX_HOURS = 168;

function normalizeHours(value) {
  const hours = Number.parseInt(value, 10);
  if (!Number.isFinite(hours) || hours <= 0) return DEFAULT_HOURS;
  return Math.min(MAX_HOURS, hours);
}

function formatDate(value) {
  if (!value) return '—';
  const date = new Date(String(value).replace(' ', 'T'));
  return Number.isNaN(date.getTime()) ? String(value) : date.toLocaleString();
}

export default function SubmittedOrdersPage() {
  const { user } = useAuth();
  const { t } = useI18n();
  const token = user?.api_token;
  const [inputHours, setInputHours] = useState(DEFAULT_HOURS);
  const [appliedHours, setAppliedHours] = useState(DEFAULT_HOURS);
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [checkingOrderId, setCheckingOrderId] = useState(null);
  const [error, setError] = useState('');
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success',
  });

  const loadOrders = useCallback(async (hours) => {
    if (!token) return;
    setLoading(true);
    setError('');
    try {
      const payload = await fetchSubmittedOrders(token, hours);
      setOrders(Array.isArray(payload.orders) ? payload.orders : []);
      setAppliedHours(payload.hours || hours);
      setInputHours(payload.hours || hours);
    } catch (requestError) {
      setError(requestError.message || t('Ошибка загрузки отправленных заказов'));
    } finally {
      setLoading(false);
    }
  }, [token, t]);

  useEffect(() => {
    loadOrders(DEFAULT_HOURS);
  }, [loadOrders]);

  const applyPeriod = () => {
    const hours = normalizeHours(inputHours);
    setInputHours(hours);
    loadOrders(hours);
  };

  const checkNow = async (order) => {
    setCheckingOrderId(order.order_id);
    try {
      await checkSubmittedOrderNow(token, order.order_id);
      setSnackbar({
        open: true,
        severity: 'success',
        message: t('Статус заказа проверен'),
      });
      await loadOrders(appliedHours);
    } catch (requestError) {
      setSnackbar({
        open: true,
        severity: 'error',
        message: requestError.message || t('Ошибка проверки заказа'),
      });
    } finally {
      setCheckingOrderId(null);
    }
  };

  if (!user || user.role !== 'admin') {
    return <Alert severity="error">{t('Доступ запрещён. Только для администратора.')}</Alert>;
  }

  return (
    <Box>
      <Box
        sx={{
          display: 'flex',
          alignItems: { xs: 'stretch', md: 'center' },
          flexDirection: { xs: 'column', md: 'row' },
          gap: 1.5,
          mb: 2,
        }}
      >
        <Box>
          <Typography variant="h5" sx={{ fontWeight: 700 }}>
            {t('Отправленные заказы')}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {t('Заказы в обработке, уже отправленные провайдеру')}
          </Typography>
        </Box>
        <Box sx={{ flex: 1 }} />
        <TextField
          label={t('Период, часов')}
          type="number"
          size="small"
          value={inputHours}
          onChange={(event) => setInputHours(event.target.value)}
          inputProps={{ min: 1, max: MAX_HOURS }}
          sx={{ width: 150 }}
        />
        <Button
          variant="outlined"
          startIcon={<RefreshIcon />}
          onClick={applyPeriod}
          disabled={loading || checkingOrderId !== null}
        >
          {t('Показать')}
        </Button>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      <Alert severity="info" sx={{ mb: 2 }}>
        {t('Найдено заказов за период: {count}').replace('{count}', orders.length)}
      </Alert>

      <TableContainer component={Paper}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>{t('Номер заказа')}</TableCell>
              <TableCell>{t('Дата и время оплаты')}</TableCell>
              <TableCell>{t('Сервис')}</TableCell>
              <TableCell>{t('Номер пополнения')}</TableCell>
              <TableCell align="right">{t('Пополняемая сумма')}</TableCell>
              <TableCell>{t('Провайдер')}</TableCell>
              <TableCell>{t('ID провайдера')}</TableCell>
              <TableCell>{t('Последнее обновление')}</TableCell>
              <TableCell>{t('Следующая попытка')}</TableCell>
              <TableCell align="right">{t('Действие')}</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={10} align="center" sx={{ py: 6 }}>
                  <CircularProgress />
                </TableCell>
              </TableRow>
            ) : orders.length === 0 ? (
              <TableRow>
                <TableCell colSpan={10} align="center" sx={{ py: 6, color: 'text.secondary' }}>
                  {t('Отправленных заказов за выбранный период нет')}
                </TableCell>
              </TableRow>
            ) : orders.map((order) => {
              const checking = checkingOrderId === order.order_id;
              return (
                <TableRow key={order.job_id} hover>
                  <TableCell>
                    <Typography sx={{ fontWeight: 600 }}>
                      {order.external_order_id || `#${order.order_id}`}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      ID: {order.order_id}
                    </Typography>
                  </TableCell>
                  <TableCell>{formatDate(order.payment_time)}</TableCell>
                  <TableCell>{order.service_name || '—'}</TableCell>
                  <TableCell>{order.phone_dist || '—'}</TableCell>
                  <TableCell align="right">{order.amount_dist ?? '—'}</TableCell>
                  <TableCell><Chip size="small" variant="outlined" label={order.provider || '—'} /></TableCell>
                  <TableCell>{order.provider_order_id || order.local_id || '—'}</TableCell>
                  <TableCell>{formatDate(order.updated_at)}</TableCell>
                  <TableCell>{formatDate(order.next_attempt_at)}</TableCell>
                  <TableCell align="right">
                    <Button
                      size="small"
                      variant="contained"
                      startIcon={checking ? <CircularProgress size={16} color="inherit" /> : <SyncIcon />}
                      disabled={!order.can_check_now || checkingOrderId !== null}
                      onClick={() => checkNow(order)}
                    >
                      {checking ? t('Проверка...') : t('Проверить сейчас')}
                    </Button>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={5000}
        onClose={() => setSnackbar((current) => ({ ...current, open: false }))}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert
          severity={snackbar.severity}
          onClose={() => setSnackbar((current) => ({ ...current, open: false }))}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}
